import { ClassType, DateStep, EntityType, formatDays } from '@frontend/common';
import { FromToDateInput } from '@frontend/components/basic-forms';
import { DefaultSecondaryButton, StatusBadge } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { ReportClient, ScheduleClient } from '@frontend/report/api';
import { reportRequest } from '@frontend/report/events';
import { Report, Schedule } from '@frontend/report/types';
import React from 'react';

import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { GenerateModalButton } from './generate-modal-button';
import HistoryList from './history-list/history-list.component';
import useReportModalDetail from './report-detail-modal.controller';
import { CreateScheduleModal } from './schedule/create-schedule-modal.component';

/** Header/actions that previously lived on DetailModal `customButton` — sit inside the Details card. */
const ReportDetailActions = ({ report }: { report: Report }) => {
    const viewProps = useReportModalDetail();
    return (
        <div className='flex w-full items-start justify-between gap-3'>
            <div className='flex gap-3'>
                <GenerateModalButton
                    onClick={() => viewProps.onClick(report)}
                    text={CommonMessage.OBJECTS.REPORT.DEFAULT}
                />
                <DefaultSecondaryButton
                    text={CommonMessage.OBJECTS.SCHEDULE.DEFAULT}
                    onClick={() => viewProps.openModal(viewProps.scheduleModalId, () => <CreateScheduleModal report_id={report.id} />)}
                />
            </div>
            <FromToDateInput
                onChange={(from, to) => {
                    viewProps.setFromDate(from);
                    viewProps.setToDate(to);
                    if (from !== null || to !== null) {
                        viewProps.setPeriod(DateStep.CUSTOM);
                    }
                    if (from === null && to === null) {
                        viewProps.setPeriod(report.period);
                    }
                }}
                onClick={(e) => viewProps.setPeriod(e as DateStep)}
                period={viewProps.period}
            />
        </div>
    );
};

export const ReportDetailsCard = ({ report }: { report: Report }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
            <PlatformDetailGrid
                object={report}
                cols={2}
                order={['name', 'description']}
                valueOverwrite={
                    new Map<string, (obj: Report) => React.ReactNode>([
                        ['name', (obj) => (locale ? getCorrectTranslation(locale, obj, 'name') : obj.name)],
                        ['description', (obj) => (locale ? getCorrectTranslation(locale, obj, 'description') : obj.description)]
                    ])
                }
            />
            <ReportDetailActions report={report} />
        </div>
    );
};

export const ReportUserHistorySection = ({ report }: { report: Report }) => {
    const currentUser = useAppSelector((state) => state.users.current);
    return (
        <HistoryList
            key='user-history-list'
            user_id={currentUser?.id}
            report_id={report.id}
        />
    );
};

export const ReportHistorySection = ({ report }: { report: Report }) => (
    <HistoryList
        key='general-history-list'
        report_id={report.id}
    />
);

export const ReportSchedulesSection = ({ report }: { report: Report }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<Schedule>
            fetch={(arg) => ScheduleClient.fetchSchedules({ ...arg, account_id: report.account_id, report_id: report.id })}
            dispatch={dispatch}
            columns={{
                columns: ['name', 'receivers', 'status', 'every_days', 'last_executed_timestamp'],
                headerOverwrite: new Map([
                    ['every_days', CommonMessage.OBJECTS.SCHEDULE.RUNS_EVERY],
                    ['status', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                ]),
                customCells: new Map([
                    [
                        'receivers',
                        (obj) => {
                            if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={obj.row.original.receivers[0]}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={obj.row.original.receivers[0]}
                                            entityType={EntityType.USER}
                                            key={obj.row.original.receivers[0]}
                                        />
                                        {obj.row.original.receivers.length > 1 && (
                                            <MultiEntityButton<string>
                                                entities={obj.row.original.receivers.slice(1)}
                                                render={(id) => (
                                                    <PlatformObjectBadge
                                                        id={id}
                                                        entityType={EntityType.USER}
                                                        key={id}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    ['every_days', (obj) => <td className={defaultCellClassName}>{formatDays(obj.row.original.every_days)}</td>],
                    [
                        'status',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <StatusBadge
                                    value={
                                        obj.row.original.enabled ? CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED : CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED
                                    }
                                    colorMap={
                                        new Map([
                                            [[CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED], ClassType.SUCCESS],
                                            [[CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED], ClassType.WARNING]
                                        ])
                                    }
                                />
                            </td>
                        )
                    ]
                ])
            }}
            type={EntityType.SCHEDULE}
            tableProps={{ defaultHeight: true, hideFooter: true }}
        />
    );
};

export const reportReactiveDetail: ReactiveDetailRegistryEntry<Report, Report> = {
    entityType: EntityType.REPORT,
    resolveFunction: reportRequest,
    fetchExtended: ({ object }) => ReportClient.fetchReport(object.account_id, object.id),
    modalClassName: 'w-full lg:w-2/3 max-w-[900px]',
    title: ({ object, extended }) => `Report: ${extended?.name ?? object.name}`,
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) =>
                view.extended ? (
                    <>
                        <ReportDetailsCard report={view.extended} />
                        <ReportUserHistorySection report={view.extended} />
                    </>
                ) : null
        },
        {
            id: 'history',
            label: CommonMessage.OBJECTS.TRANSACTION.HISTORY,
            render: (view) => (view.extended ? <ReportHistorySection report={view.extended} /> : null)
        },
        {
            id: 'schedules',
            label: CommonMessage.OBJECTS.SCHEDULE.PLURAL,
            render: (view) => (view.extended ? <ReportSchedulesSection report={view.extended} /> : null)
        }
    ]
};
